import { Dimensions, Measure } from './Measurement'
import { useOptionalMeasurements } from '../context/MeasurementContext'
import { convertMeasurement } from '../lib/measurement'
import { readCsv, csvCell } from '../lib/measurementExchange'
import { parseMeasurementCsv, parseProductJson } from '../lib/measurementExchange'
import React, { useState, useRef } from 'react'
import { Product } from '../services/product.service'
import { apiFetch } from '../utils/apiFetch'
import { useQueryClient } from '@tanstack/react-query'
import {
    ArrowUpTrayIcon,
    DocumentTextIcon,
    XMarkIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    CodeBracketIcon,
    SparklesIcon,
    CloudArrowUpIcon
} from '@heroicons/react/24/outline'

interface ProductImportButtonProps {
    existingProducts?: Product[]
    onImportSuccess?: (msg: string) => void
}

const DEFAULT_COLORS = [
    '#6366f1', '#059669', '#d97706', '#db2777', '#0891b2',
    '#ea580c', '#0d9488', '#4f46e5', '#9333ea', '#2563eb'
]

export default function ProductImportButton({
    existingProducts = [],
    onImportSuccess
}: ProductImportButtonProps) {
    const system = useOptionalMeasurements()?.system ?? 'metric'
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [activeInputTab, setActiveInputTab] = useState<'file' | 'text'>('file')
    const [rawText, setRawText] = useState('')
    const [parsedProducts, setParsedProducts] = useState<Partial<Product>[] | null>(null)
    const [parseError, setParseError] = useState<string | null>(null)
    const [importStrategy, setImportStrategy] = useState<'append' | 'skip_duplicates'>('append')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isDragging, setIsDragging] = useState(false)

    const fileInputRef = useRef<HTMLInputElement>(null)
    const queryClient = useQueryClient()

    // Reset dialog state on open/close
    const handleOpenModal = () => {
        setIsModalOpen(true)
        setRawText('')
        setParsedProducts(null)
        setParseError(null)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setRawText('')
        setParsedProducts(null)
        setParseError(null)
    }

    // Parse raw text or file content (supports JSON or CSV)
    const handleParseContent = (text: string) => {
        setRawText(text)
        setParseError(null)
        setParsedProducts(null)

        if (!text.trim()) return

        try {
            const trimmed = text.trim()
            const items = trimmed.startsWith('[') || trimmed.startsWith('{')
                ? parseProductJson(trimmed) : parseMeasurementCsv(trimmed, 'catalog')
            setParsedProducts(items.map((item, index) => ({ ...item, color: item.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length], status: item.status || 'active' })))
        } catch (error) {
            setParseError((error as Error).message)
        }
    }

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        const reader = new FileReader()
        reader.onload = (event) => {
            const content = event.target?.result as string
            if (content) handleParseContent(content)
        }
        reader.readAsText(file)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
        const file = e.dataTransfer.files?.[0]
        if (!file) return

        const reader = new FileReader()
        reader.onload = (event) => {
            const content = event.target?.result as string
            if (content) handleParseContent(content)
        }
        reader.readAsText(file)
    }

    // Legacy files without units: catalog cm/kg; explicit units always take priority.
    // Template generators
    const handleDownloadCsvTemplate = () => {
        let template = 'SKU;Nom_Produit;Description;Longueur_cm;Largeur_cm;Hauteur_cm;Poids_kg;ADR;Froid;Empilable;Au_Sol;Rotatable\n' +
            'PAL-EUR-100;Palette Europe Jus de Fruit;Palette de jus de pomme 1L;120;80;144;650;non;non;oui;non;oui\n' +
            'CARTON-HIGH;Carton Électronique SEN-80;Sensible aux chocs;60;40;40;15.5;non;non;oui;non;oui\n' +
            'CHIM-ADR-01;Fût Solvant Chimique UN1203;Matières inflammables classe 3;50;50;85;120;oui;non;non;oui;non\n'

        if (system === 'imperial') {
            const rows = readCsv(template)
            rows[0] = rows[0].map(h => h.replace('_cm', '_in').replace('_kg', '_lb'))
            for (const row of rows.slice(1)) {
                for (const i of [3, 4, 5]) row[i] = String(convertMeasurement(Number(row[i]), 'cm', 'in'))
                row[6] = String(convertMeasurement(Number(row[6]), 'kg', 'lb'))
            }
            template = rows.map(row => row.map(csvCell).join(';')).join('\n')
        }
        const blob = new Blob(['\uFEFF' + template], { type: 'text/csv;charset=utf-8;' })
        downloadFile(blob, 'modele_import_produits_tms.csv')
    }

    const handleDownloadJsonTemplate = () => {
        const template = [
            {
                sku: 'PAL-EUR-100',
                name: 'Palette Europe Jus de Fruit',
                description: 'Palette de jus de pomme 1L',
                dimension_unit: 'cm', weight_unit: 'kg', length: 120,
                width: 80,
                height: 144,
                weight: 650,
                is_dangerous_goods: false,
                temperature_controlled: false,
                stackable: true,
                must_be_on_floor: false,
                rotatable: true
            },
            {
                sku: 'CHIM-ADR-01',
                name: 'Fût Solvant Chimique UN1203',
                description: 'Matières inflammables classe 3',
                dimension_unit: 'cm', weight_unit: 'kg', length: 50,
                width: 50,
                height: 85,
                weight: 120,
                is_dangerous_goods: true,
                dangerous_goods_class: '3',
                un_number: 'UN1203',
                temperature_controlled: false,
                stackable: false,
                must_be_on_floor: true,
                rotatable: false
            }
        ]

        if (system === 'imperial') {
            for (const item of template) {
                item.length = convertMeasurement(item.length, 'cm', 'in')
                item.width = convertMeasurement(item.width, 'cm', 'in')
                item.height = convertMeasurement(item.height, 'cm', 'in')
                item.weight = convertMeasurement(item.weight, 'kg', 'lb')
                item.dimension_unit = 'in'; item.weight_unit = 'lb'
            }
        }
        const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json;charset=utf-8;' })
        downloadFile(blob, 'modele_import_produits_tms.json')
    }

    const downloadFile = (blob: Blob, name: string) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    // Execute Import Submit
    const handleConfirmImport = async () => {
        if (!parsedProducts || parsedProducts.length === 0) return

        setIsSubmitting(true)

        try {
            const existingSkus = new Set(existingProducts.map(p => (p.sku || '').toLowerCase()))
            let itemsToImport = parsedProducts

            if (importStrategy === 'skip_duplicates') {
                itemsToImport = parsedProducts.filter(p => !p.sku || !existingSkus.has(p.sku.toLowerCase()))
            }

            let successCount = 0

            // Execute product creation requests sequentially/in parallel batch
            for (const productPayload of itemsToImport) {
                await apiFetch('/api/internal/tms/products', {
                    method: 'POST',
                    body: JSON.stringify(productPayload)
                })
                successCount++
            }

            queryClient.invalidateQueries({ queryKey: ['products'] })

            if (onImportSuccess) {
                onImportSuccess(`${successCount} produit(s) importé(s) avec succès au catalogue.`)
            }

            handleCloseModal()
        } catch (err: any) {
            setParseError(`Erreur lors de l'enregistrement des produits: ${err.message || err}`)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            {/* Modern Ergonomic Import Button */}
            <button
                type="button"
                onClick={handleOpenModal}
                title="Importer des produits depuis un fichier CSV ou JSON"
                className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 hover:border-orange-300 dark:hover:border-orange-600 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow transition-all duration-200 cursor-pointer"
            >
                <ArrowUpTrayIcon className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <span>Importer</span>
            </button>

            {/* Product Import Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full border border-slate-200 dark:border-slate-800 overflow-hidden animate-fade-in-down">
                        {/* Header */}
                        <div className="cargo-bg px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-950/60 border border-orange-200 dark:border-orange-800 flex items-center justify-center flex-shrink-0">
                                    <ArrowUpTrayIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                </div>
                                <div>
                                    <h3 className="font-extrabold text-slate-900 dark:text-white text-base uppercase tracking-tight">
                                        Importation de Produits
                                    </h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                                        CSV / JSON avec unités explicites. Ancien format sans unité : cm/kg.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleCloseModal}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto custom-scrollbar">
                            {/* Top Template Downloads Bar */}
                            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                                <div className="text-xs text-slate-600 dark:text-slate-300">
                                    <span className="font-bold text-slate-900 dark:text-white">Formats acceptés :</span> CSV, TXT ou JSON
                                    <p className="text-[11px] text-slate-400">Colonnes : SKU; Nom; Description; Long; Larg; Haut; Poids</p>
                                </div>
                                <div className="flex gap-2 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={handleDownloadCsvTemplate}
                                        className="px-2.5 py-1.5 text-xs font-bold text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950/40 border border-orange-200 dark:border-orange-800 rounded-lg transition-colors flex items-center gap-1"
                                    >
                                        <DocumentTextIcon className="w-3.5 h-3.5" />
                                        Modèle CSV
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDownloadJsonTemplate}
                                        className="px-2.5 py-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg transition-colors flex items-center gap-1"
                                    >
                                        <CodeBracketIcon className="w-3.5 h-3.5" />
                                        Modèle JSON
                                    </button>
                                </div>
                            </div>

                            {/* Mode Tabs: File Upload vs Raw Text */}
                            <div className="flex border-b border-slate-200 dark:border-slate-700">
                                <button
                                    type="button"
                                    onClick={() => setActiveInputTab('file')}
                                    className={`px-4 py-2 text-xs font-extrabold uppercase tracking-wider flex items-center gap-2 border-b-2 transition-colors ${activeInputTab === 'file'
                                        ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                                        : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                                        }`}
                                >
                                    <CloudArrowUpIcon className="w-4 h-4" />
                                    Glisser / Déposer Fichier
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveInputTab('text')}
                                    className={`px-4 py-2 text-xs font-extrabold uppercase tracking-wider flex items-center gap-2 border-b-2 transition-colors ${activeInputTab === 'text'
                                        ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                                        : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                                        }`}
                                >
                                    <DocumentTextIcon className="w-4 h-4" />
                                    Coller du Texte Brut
                                </button>
                            </div>

                            {/* TAB 1: FILE UPLOAD ZONE */}
                            {activeInputTab === 'file' && (
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                                    onDragLeave={() => setIsDragging(false)}
                                    onDrop={handleDrop}
                                    className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all cursor-pointer ${isDragging
                                        ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-950/30 scale-[0.99]'
                                        : 'border-slate-300 dark:border-slate-700 hover:border-orange-400 bg-slate-50/50 dark:bg-slate-800/40'
                                        }`}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept=".csv,.json,.txt"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                    <CloudArrowUpIcon className="w-10 h-10 mx-auto text-orange-500 mb-2 animate-bounce-short" />
                                    <p className="text-xs font-bold text-slate-800 dark:text-slate-100">
                                        Cliquez ici pour sélectionner un fichier CSV ou JSON
                                    </p>
                                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                                        ou glissez-déposez votre fichier dans cette zone
                                    </p>
                                </div>
                            )}

                            {/* TAB 2: RAW TEXT AREA */}
                            {activeInputTab === 'text' && (
                                <div>
                                    <textarea
                                        rows={6}
                                        value={rawText}
                                        onChange={(e) => handleParseContent(e.target.value)}
                                        placeholder={`Exemple CSV :\nSKU;Nom_Produit;Description;Longueur_cm;Largeur_cm;Hauteur_cm;Poids_kg\nART-001;Carton Électronique;Sensible;60;40;40;12`}
                                        className="w-full text-xs font-mono p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 placeholder:text-slate-400"
                                    />
                                </div>
                            )}

                            {/* PARSE ERROR FEEDBACK */}
                            {parseError && (
                                <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                                    <ExclamationTriangleIcon className="w-4.5 h-4.5 flex-shrink-0" />
                                    <span className="whitespace-pre-line">{parseError}</span>
                                </div>
                            )}

                            {/* PARSED PREVIEW TABLE */}
                            {parsedProducts && parsedProducts.length > 0 && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                                            <CheckCircleIcon className="w-4 h-4" />
                                            {parsedProducts.length} produit(s) détecté(s) prêts à être créés
                                        </span>

                                        <div className="flex items-center gap-3 text-xs">
                                            <label className="flex items-center gap-1 cursor-pointer text-slate-600 dark:text-slate-300 font-semibold">
                                                <input
                                                    type="radio"
                                                    name="importStrategy"
                                                    checked={importStrategy === 'append'}
                                                    onChange={() => setImportStrategy('append')}
                                                    className="text-orange-500 focus:ring-orange-500"
                                                />
                                                Tout importer
                                            </label>
                                            <label className="flex items-center gap-1 cursor-pointer text-slate-600 dark:text-slate-300 font-semibold">
                                                <input
                                                    type="radio"
                                                    name="importStrategy"
                                                    checked={importStrategy === 'skip_duplicates'}
                                                    onChange={() => setImportStrategy('skip_duplicates')}
                                                    className="text-orange-500 focus:ring-orange-500"
                                                />
                                                Ignorer doublons SKU
                                            </label>
                                        </div>
                                    </div>

                                    <div className="max-h-44 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl">
                                        <table className="w-full text-xs text-left">
                                            <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold sticky top-0">
                                                <tr>
                                                    <th className="p-2">SKU</th>
                                                    <th className="p-2">Nom du Produit</th>
                                                    <th className="p-2 text-right">Dimensions</th>
                                                    <th className="p-2 text-right">Poids</th>
                                                    <th className="p-2">Spécificités</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                {parsedProducts.map((p, idx) => (
                                                    <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                                        <td className="p-2 font-mono font-semibold text-slate-900 dark:text-white">
                                                            {p.sku || '—'}
                                                        </td>
                                                        <td className="p-2 text-slate-700 dark:text-slate-300 font-medium">
                                                            {p.name}
                                                        </td>
                                                        <td className="p-2 text-right font-mono text-slate-600 dark:text-slate-400">
                                                            <Dimensions length={p.length} width={p.width} height={p.height} from={p.dimension_unit || "mm"} />
                                                        </td>
                                                        <td className="p-2 text-right font-mono text-slate-600 dark:text-slate-400">
                                                            <Measure value={p.weight} from={p.weight_unit || "kg"} quantity="mass" />
                                                        </td>
                                                        <td className="p-2">
                                                            <div className="flex gap-1">
                                                                {p.is_dangerous_goods && <span className="px-1 py-0.2 rounded text-[9px] font-black bg-red-100 text-red-700">ADR</span>}
                                                                {p.temperature_controlled && <span className="px-1 py-0.2 rounded text-[9px] font-black bg-cyan-100 text-cyan-700">FROID</span>}
                                                                {p.must_be_on_floor && <span className="px-1 py-0.2 rounded text-[9px] font-black bg-purple-100 text-purple-700">SOL</span>}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer Actions */}
                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleCloseModal}
                                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                type="button"
                                disabled={!parsedProducts || parsedProducts.length === 0 || isSubmitting}
                                onClick={handleConfirmImport}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:-translate-y-0.5 transition-all"
                            >
                                {isSubmitting ? (
                                    <>
                                        <SparklesIcon className="w-4 h-4 animate-spin" />
                                        Importation en cours…
                                    </>
                                ) : (
                                    <>
                                        <ArrowUpTrayIcon className="w-4 h-4" />
                                        Valider l&apos;importation {parsedProducts ? `(${parsedProducts.length})` : ''}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
